import { Injectable } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from './tasks.actions';
import * as fromStore from '../../store/tasks';
import * as fromAuth from '../../../../store/auth';
import { switchMap, map, catchError, withLatestFrom, concatMap } from 'rxjs/operators';
import { AuditingService } from '../../services/auditing.service';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuditingState } from '../auditing.reducer';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuditorReadyDialogComponent } from "../../components/auditor-ready-dialog/auditor-ready-dialog.component";
import { IdleService } from "../../services/idle.service";

@Injectable()
export class TasksEffects {
  constructor(
    private actions$: Actions,
    private auditing: AuditingService,
    private store: Store<{ auditing: AuditingState }>,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private idleService: IdleService,
  ) {
    const countdown = 300;

    this.idleService.idleStart.subscribe(() => {
      AuditorReadyDialogComponent.open(this.dialog, countdown);
    });

    this.idleService.timeout.subscribe(() => {
      this.store.dispatch(
        fromStore.setIsAuditorReady({ isAuditorReady: false })
      );
    });
  }

  // Three requests rather than one join: the queue is the slow one, and the assigned tasks and the
  // side panel have no reason to wait behind it.
  loadAssignedTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.initPage),
      switchMap(() =>
        this.auditing.getTasks().pipe(
          map(tasks => fromActions.loadAssignedTasksSuccess({ tasks })),
          catchError((error: Error) => of(fromActions.loadTasksError({ error }))),
        ),
      ),
    ),
  );

  loadAvailableTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.initPage),
      switchMap(() =>
        this.auditing.getAvailableTasks().pipe(
          map(availableTasks => fromActions.loadAvailableTasksSuccess({ availableTasks })),
          catchError((error: Error) => of(fromActions.loadTasksError({ error }))),
        ),
      ),
    ),
  );

  loadAuditorState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.initPage),
      switchMap(() =>
        this.auditing.getNotificationState().pipe(
          map(notifyInfo => {
            if (!this.idleService.isRunning() && notifyInfo.isAvailable) this.idleService.startWatching(2400, 300);
            return fromActions.loadAuditorStateSuccess({ notifyInfo });
          }),
          catchError((error: Error) => of(fromActions.loadTasksError({ error }))),
        ),
      ),
    ),
  );

  setNotify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setShouldNotify),
      concatMap(({ shouldNotify }) =>
        this.auditing.setNotificationState(shouldNotify).pipe(
          map(() => fromActions.setShouldNotifySuccess({ newShouldNotify: shouldNotify })),
          catchError((error: Error) => of(fromActions.setShouldNotifyFailed({ error }))),
        ),
      ),
    ),
  );

  setIsAuditorReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setIsAuditorReady),
      concatMap(({ isAuditorReady }) =>
        this.auditing.setAuditorAvailabilityState(isAuditorReady).pipe(
          map(() => {
            if (!this.idleService.isRunning() && isAuditorReady)
              this.idleService.startWatching(1620, 180);
            else if (!isAuditorReady)
              this.idleService.stopWatching();

            return fromActions.setIsAuditorReadySuccess({ newIsAuditorReady: isAuditorReady });
          }),
          catchError((error: Error) => of(fromActions.setIsAuditorReadyFailed({ error }))),
        ),
      ),
    ),
  );

  takeTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.takeTask),
      withLatestFrom(this.store.select(fromAuth.selectUser)),
      switchMap(([{ task }, user]) =>
        this.auditing.takeTask(task.id).pipe(
          map(res => fromActions.takeTaskSuccess({ task: res, assigneeUsername: user.username })),
          catchError((error: Error) => {
            return of(fromActions.takeTaskFailed({ error }));
          }),
        ),
      ),
    ),
  );

  takeAllTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.takeAllTasks),
      withLatestFrom(this.store.select(fromAuth.selectUser)),
      switchMap(([{ tasks }, user]) =>
        this.auditing.takeAllTasks(tasks[0].branchId, tasks[0].periodId, tasks.map(task => task.id)).pipe(
          map(res => fromActions.takeAllTasksSuccess({ tasks: res, assigneeUsername: user.username })),
          catchError((error: Error) => {
            return of(fromActions.takeAllTasksFailed({ error }));
          }),
        ),
      ),
    ),
  );

  giveUpTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.giveUpTask),
      withLatestFrom(this.store.select(fromAuth.selectUser)),
      switchMap(([{ task, comment }, user]) =>
        this.auditing.giveUpTask(task.id, comment).pipe(
          map(() => fromActions.giveUpTaskSuccess({ task })),
          catchError((error: any) => {
            const errorMessage =
              'error' in error && (error['error'] as string) ? error['error'] : 'Failed to give up task... Please refresh the page.';
            this.snack.open(errorMessage, 'OK', {
              duration: 5000,
            });
            return of(fromActions.giveUpTaskFailed({ error }));
          }),
        ),
      ),
    ),
  );
}
